import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Filter, Plus, X, Save } from 'lucide-react';
import { toast } from 'sonner';

const AdvancedFilterBuilder = ({ onFiltersChange, reducedMotion }) => {
  const [filters, setFilters] = useState([
    { id: 1, field: 'campaign_id', operator: 'equals', value: '' }
  ]);
  const [savedFilters, setSavedFilters] = useState([]);

  const fieldOptions = [
    { value: 'campaign_id', label: 'Campaign' },
    { value: 'status', label: 'Call Status' },
    { value: 'disposition', label: 'Disposition' },
    { value: 'duration_ms', label: 'Duration (ms)' },
    { value: 'sentiment', label: 'Sentiment' },
    { value: 'intent', label: 'Intent' },
    { value: 'carrier', label: 'Carrier' },
    { value: 'province', label: 'Province' }
  ];

  const operatorOptions = [
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '!=' },
    { value: 'greater_than', label: '>' },
    { value: 'less_than', label: '<' },
    { value: 'contains', label: 'contains' },
    { value: 'in', label: 'in' }
  ];

  const handleAddFilter = () => {
    const newFilter = { id: Date.now(), field: 'campaign_id', operator: 'equals', value: '' };
    setFilters([...filters, newFilter]);
  };

  const handleRemoveFilter = (id) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  const handleFilterChange = (id, field, value) => {
    setFilters(filters.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const handleApplyFilters = () => {
    // Convert filters to API-compatible format
    const filterObj = {};
    filters.forEach(f => {
      if (f.value) {
        filterObj[f.field] = { operator: f.operator, value: f.value };
      }
    });
    onFiltersChange && onFiltersChange(filterObj);
    toast.success('Filters applied across all charts');
  };

  const handleSaveFilterSet = () => {
    const filterName = prompt('Enter a name for this filter set:');
    if (filterName) {
      setSavedFilters([...savedFilters, { name: filterName, filters: [...filters] }]);
      toast.success(`Filter set "${filterName}" saved`);
    }
  };

  const handleLoadFilterSet = (filterSet) => {
    setFilters(filterSet.filters);
    toast.success(`Filter set "${filterSet.name}" loaded`);
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="advanced-filter-builder-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Filter className="w-5 h-5 text-teal-400" />
          Advanced Filter Builder
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Build complex multi-dimensional filters that apply across all charts
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filter Rules */}
        <div className="space-y-3 mb-4">
          {filters.map((filter, index) => (
            <div
              key={filter.id}
              className="flex items-center gap-2 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50"
              style={{
                animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
              }}
              data-testid={`filter-row-${filter.id}`}
            >
              <select
                value={filter.field}
                onChange={(e) => handleFilterChange(filter.id, 'field', e.target.value)}
                className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
                data-testid={`filter-field-${filter.id}`}
              >
                {fieldOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <select
                value={filter.operator}
                onChange={(e) => handleFilterChange(filter.id, 'operator', e.target.value)}
                className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
                data-testid={`filter-operator-${filter.id}`}
              >
                {operatorOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <input
                type="text"
                value={filter.value}
                onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                placeholder="Value"
                className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500"
                data-testid={`filter-value-${filter.id}`}
              />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFilter(filter.id)}
                className="text-red-400 hover:text-red-300"
                data-testid={`remove-filter-${filter.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleAddFilter}
            variant="outline"
            className="text-slate-300 border-slate-600 hover:bg-slate-800"
            data-testid="add-filter-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Filter
          </Button>
          <Button
            onClick={handleApplyFilters}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="apply-filters-button"
          >
            Apply Filters
          </Button>
          <Button
            onClick={handleSaveFilterSet}
            variant="outline"
            className="text-slate-300 border-slate-600 hover:bg-slate-800"
            data-testid="save-filter-set-button"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Filter Set
          </Button>
        </div>

        {/* Saved Filter Sets */}
        {savedFilters.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-slate-300 mb-3">Saved Filter Sets</h4>
            <div className="flex flex-wrap gap-2">
              {savedFilters.map((filterSet, index) => (
                <button
                  key={index}
                  onClick={() => handleLoadFilterSet(filterSet)}
                  className="px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                  data-testid={`load-filter-set-${index}`}
                >
                  {filterSet.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdvancedFilterBuilder;